import React from 'react';
import { Users, Crown, ShieldCheck, CalendarClock, ListChecks, Lock } from 'lucide-react';
import { RatingBadge } from './RatingBadge';
import { imgUrl, fmtDate, daysUntil } from './groupMeta';

export const GroupCard = ({ group: g, onOpen, index = 0 }) => {
  const due = daysUntil(g.next_inspection_due);
  const archived = g.status === 'archived';
  return (
    <button type="button" onClick={() => onOpen(g)} data-testid={`group-card-${g.id}`}
      style={{ animationDelay: `${Math.min(index, 12) * 45}ms` }}
      className={`group text-left relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0A0A12]/80 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-white/[0.18] animate-[jewelFadeUp_0.45s_ease-out_both] ${archived ? 'opacity-70' : ''}`}>
      <div className="relative h-36 overflow-hidden">
        {g.photo_url ? <img src={imgUrl(g.photo_url)} alt="" className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${archived ? 'grayscale' : ''}`} />
          : <div className="w-full h-full" style={{ background: `radial-gradient(circle at 30% 20%, ${g.accent_color}55, transparent 60%), #0d0d18` }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] via-[#0A0A12]/40 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-1.5">
          <RatingBadge rating={g.latest_rating} testId={`group-rating-${g.id}`} />
          {archived && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-black/60 border border-white/15 text-white/70"><Lock className="w-3 h-3" /> Archived</span>}
        </div>
        {g.inspection_overdue && <span className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-semibold bg-rose-500/25 border border-rose-400/50 text-rose-100 animate-pulse" data-testid={`group-overdue-${g.id}`}>Inspection overdue</span>}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-[9px] tracking-[0.2em] uppercase font-semibold" style={{ color: g.accent_color }}>{g.type}</p>
          <h3 className="text-lg text-white font-semibold leading-tight truncate">{g.name}</h3>
        </div>
      </div>
      <div className="p-4 pt-3 space-y-2.5">
        {g.tagline && <p className="text-xs text-white/45 line-clamp-1">{g.tagline}</p>}
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <p className="flex items-center gap-1.5 text-white/60 truncate"><Crown className="w-3.5 h-3.5 text-amber-300/80 flex-shrink-0" />{g.leader?.name || 'No leader set'}</p>
          <p className="flex items-center gap-1.5 text-white/60 truncate"><ShieldCheck className="w-3.5 h-3.5 text-cyan-300/80 flex-shrink-0" />{g.oversight_name || 'No oversight'}</p>
          <p className="flex items-center gap-1.5 text-white/45"><Users className="w-3.5 h-3.5 flex-shrink-0" />{(g.members || []).length} on staff list</p>
          <p className="flex items-center gap-1.5 text-white/45"><ListChecks className="w-3.5 h-3.5 flex-shrink-0" />{g.open_actions} open actions</p>
        </div>
        <div className="pt-2.5 border-t border-white/[0.05] flex items-center justify-between text-[10px]">
          <span className="text-white/35">Last inspected {g.latest_inspection_at ? fmtDate(g.latest_inspection_at) : 'never'}</span>
          {g.next_inspection_due && !archived && (
            <span className={`flex items-center gap-1 ${due < 0 ? 'text-rose-300' : due <= 14 ? 'text-amber-300' : 'text-white/45'}`}>
              <CalendarClock className="w-3 h-3" />{due < 0 ? `${-due}d overdue` : due === 0 ? 'Due today' : `Due in ${due}d`}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};
